import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import FamilyRestroomRoundedIcon from "@mui/icons-material/FamilyRestroomRounded";
import { useNavigate } from "react-router-dom";

import LoggedInLayout from "../../layout/LoggedInLayout";
import { fetchParentStudentLinks } from "../../services/api";

export const SELECTED_PARENT_CHILD_STORAGE_KEY = "parent-portal-selected-child";

const ParentChildSelector = ({ childOptions, selectedChildId, onChange }) => (
  <Box
    sx={{
      mb: 3,
      p: { xs: 2, sm: 2.5 },
      display: "flex",
      alignItems: { xs: "stretch", sm: "center" },
      justifyContent: "space-between",
      flexDirection: { xs: "column", sm: "row" },
      gap: 2,
      border: "1px solid rgba(41, 45, 91, 0.1)",
      borderRadius: "12px",
      bgcolor: "#F7F8FB",
      boxShadow: "0 10px 28px rgba(41, 45, 91, 0.06)",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <FamilyRestroomRoundedIcon sx={{ color: "#F6AA48", fontSize: 30 }} />
      <Box>
        <Typography sx={{ color: "#292D5B", fontSize: 15, fontWeight: 800 }}>
          Viewing child information
        </Typography>
        <Typography sx={{ color: "#697089", fontSize: 12.5, mt: 0.2 }}>
          Select the linked child whose information you want to view or manage.
        </Typography>
      </Box>
    </Box>

    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 290 } }}>
      <Select
        value={selectedChildId}
        onChange={(event) => onChange(event.target.value)}
        sx={{ bgcolor: "#fff", borderRadius: "9px", fontWeight: 700 }}
      >
        {childOptions.map((child) => (
          <MenuItem key={child.id} value={String(child.id)}>
            <Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                {child.name || "Student"}
              </Typography>
              <Typography sx={{ color: "#7B8498", fontSize: 11.5 }}>
                {child.email}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
);

ParentChildSelector.propTypes = {
  childOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedChildId: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ParentChildAccess = ({ render }) => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadChildren = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetchParentStudentLinks();
      const links = response?.data?.links || response?.data?.data?.links || [];
      const acceptedChildren = links
        .filter((link) => link.status === "accepted" && link.student?.id)
        .map((link) => link.student)
        .filter(
          (child, index, list) =>
            list.findIndex((item) => String(item.id) === String(child.id)) === index,
        );
      setChildren(acceptedChildren);

      const storedId = localStorage.getItem(SELECTED_PARENT_CHILD_STORAGE_KEY);
      const selected =
        acceptedChildren.find((child) => String(child.id) === storedId) ||
        acceptedChildren[0] ||
        null;
      const nextId = selected ? String(selected.id) : "";
      setSelectedChildId(nextId);
      if (nextId) localStorage.setItem(SELECTED_PARENT_CHILD_STORAGE_KEY, nextId);
      else localStorage.removeItem(SELECTED_PARENT_CHILD_STORAGE_KEY);
    } catch (loadError) {
      setChildren([]);
      setSelectedChildId("");
      setError(
        loadError?.response?.data?.message ||
          "Unable to load linked children right now.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const selectedChild = useMemo(
    () => children.find((child) => String(child.id) === selectedChildId) || null,
    [children, selectedChildId],
  );

  const handleChange = (nextId) => {
    setSelectedChildId(nextId);
    localStorage.setItem(SELECTED_PARENT_CHILD_STORAGE_KEY, nextId);
  };

  if (loading || !selectedChild) {
    return (
      <LoggedInLayout searchBar={false}>
        <Box
          sx={{
            minHeight: "70vh",
            display: "grid",
            placeItems: "center",
            p: 3,
            bgcolor: "#F7F8FB",
          }}
        >
          {loading ? (
            <CircularProgress sx={{ color: "#F6AA48" }} />
          ) : (
            <Box sx={{ maxWidth: 520, textAlign: "center" }}>
              <FamilyRestroomRoundedIcon sx={{ color: "#F6AA48", fontSize: 52 }} />
              <Typography sx={{ mt: 1, color: "#292D5B", fontSize: 20, fontWeight: 800 }}>
                {error || "No accepted linked child found"}
              </Typography>
              <Typography sx={{ mt: 1, color: "#697089", fontSize: 13.5 }}>
                Link and approve a student account before accessing child information.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/parent-portal/profile")}
                sx={{ mt: 2.5, bgcolor: "#292D5B", textTransform: "none" }}
              >
                Manage linked students
              </Button>
            </Box>
          )}
        </Box>
      </LoggedInLayout>
    );
  }

  const selector = (
    <ParentChildSelector
      childOptions={children}
      selectedChildId={selectedChildId}
      onChange={handleChange}
    />
  );

  return render({ selectedChild, selector });
};

ParentChildAccess.propTypes = {
  render: PropTypes.func.isRequired,
};

export default ParentChildAccess;
